<template>
  <BaseModal
    :is-open="isOpen"
    title="Opening Meld Requirements"
    max-width="550px"
    @close="handleClose"
  >
    <div class="opening-requirements-container">
      <!-- Info Icon -->
      <div class="info-icon">
        <span class="material-symbols-outlined">info</span>
      </div>

      <!-- Main Message -->
      <p class="main-message">
        Your team must meet the minimum point requirement for the opening meld based on your
        current score.
      </p>

      <!-- Requirements Table -->
      <div class="requirements-table">
        <h4 class="table-title">Opening Meld Point Requirements</h4>

        <div class="requirement-row" :class="{ current: isCurrentRange(0, 2500) }">
          <div class="score-range">
            <span class="range-label">0 - 2,500</span>
          </div>
          <div class="points-required">
            <span class="points-value">50</span>
            <span class="points-label">points</span>
          </div>
        </div>

        <div class="requirement-row" :class="{ current: isCurrentRange(2501, 5000) }">
          <div class="score-range">
            <span class="range-label">2,501 - 5,000</span>
          </div>
          <div class="points-required">
            <span class="points-value">90</span>
            <span class="points-label">points</span>
          </div>
        </div>

        <div class="requirement-row" :class="{ current: isCurrentRange(5001, 7500) }">
          <div class="score-range">
            <span class="range-label">5,001 - 7,500</span>
          </div>
          <div class="points-required">
            <span class="points-value">120</span>
            <span class="points-label">points</span>
          </div>
        </div>

        <div class="requirement-row" :class="{ current: isCurrentRange(7501, 10000) }">
          <div class="score-range">
            <span class="range-label">7,501 - 10,000</span>
          </div>
          <div class="points-required">
            <span class="points-value">150</span>
            <span class="points-label">points</span>
          </div>
        </div>
      </div>

      <!-- Current Team Status -->
      <div class="team-status">
        <div class="status-item">
          <span class="status-label">Your Team Score:</span>
          <span class="status-value">{{ formatNumber(teamScore) }}</span>
        </div>
        <div class="status-item highlight">
          <span class="status-label">Required Points:</span>
          <span class="status-value">{{ requiredPoints }}</span>
        </div>
      </div>

      <!-- Tips Section -->
      <div class="tips-section">
        <h4 class="tips-title">
          <span class="material-symbols-outlined">lightbulb</span>
          <span>Tips:</span>
        </h4>
        <ul class="tips-list">
          <li>Wild cards CAN be included in opening melds</li>
          <li>You can create multiple melds to reach the requirement</li>
          <li>Natural cards are worth more points (Aces = 20, K-8 = 10, 7-4 = 5)</li>
          <li>Wild cards are worth 20 points (2s) and 50 points (Jokers)</li>
        </ul>
      </div>

      <!-- Close Button -->
      <button @click="handleClose" class="btn-close">
        <span class="material-symbols-outlined">check_circle</span>
        <span>Got It</span>
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * OpeningRequirementsModal Component
 *
 * Displays opening meld point requirements based on team score.
 * Shows which range the team is currently in.
 *
 * Props:
 * - team_score: Current team score
 * - required_points: Minimum points needed for opening meld
 *
 * Emits:
 * - close: [] - Modal closed
 */

interface Props {
  isOpen: boolean
  data: {
    team_score?: number
    required_points?: number
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  close: []
}>()

/**
 * Team score
 */
const teamScore = computed(() => props.data?.team_score || 0)

/**
 * Required points for opening
 */
const requiredPoints = computed(() => {
  if (props.data?.required_points !== undefined) {
    return props.data.required_points
  }

  // Calculate based on score
  const score = teamScore.value
  if (score <= 2500) return 50
  if (score <= 5000) return 90
  if (score <= 7500) return 120
  return 150
})

/**
 * Check if score is in range
 */
function isCurrentRange(min: number, max: number): boolean {
  const score = teamScore.value
  return score >= min && score <= max
}

/**
 * Format number with commas
 */
function formatNumber(num: number): string {
  return num.toLocaleString()
}

/**
 * Handle close
 */
function handleClose(): void {
  emit('close')
}
</script>

<style scoped>
.opening-requirements-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Info Icon */
.info-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-icon .material-symbols-outlined {
  font-size: 2.5rem;
  color: #60a5fa;
}

/* Main Message */
.main-message {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  line-height: 1.6;
  margin: 0;
  max-width: 450px;
}

/* Requirements Table */
.requirements-table {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  overflow: hidden;
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1.25rem;
  text-align: center;
}

.requirement-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  transition: all 0.2s;
}

.requirement-row:last-child {
  margin-bottom: 0;
}

.requirement-row.current {
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.15), rgba(212, 175, 55, 0.15));
  border-color: rgba(232, 197, 71, 0.4);
  box-shadow: 0 0 15px rgba(232, 197, 71, 0.2);
}

.score-range {
  display: flex;
  flex-direction: column;
}

.range-label {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.requirement-row.current .range-label {
  color: #e8c547;
}

.points-required {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.points-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.requirement-row.current .points-value {
  color: #e8c547;
}

.points-label {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Team Status */
.team-status {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9375rem;
}

.status-label {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.status-value {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 700;
  font-size: 1.125rem;
}

.status-item.highlight {
  padding: 0.75rem;
  background: rgba(232, 197, 71, 0.15);
  border-radius: 8px;
  border: 1px solid rgba(232, 197, 71, 0.3);
}

.status-item.highlight .status-label,
.status-item.highlight .status-value {
  color: #e8c547;
}

/* Tips Section */
.tips-section {
  width: 100%;
  background: rgba(232, 197, 71, 0.05);
  border: 1px solid rgba(232, 197, 71, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
}

.tips-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #e8c547;
  margin: 0 0 1rem;
}

.tips-title .material-symbols-outlined {
  font-size: 1.25rem;
}

.tips-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.tips-list li {
  padding-left: 1.5rem;
  position: relative;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

.tips-list li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: #e8c547;
  font-weight: 600;
}

/* Close Button */
.btn-close {
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
  margin-top: 0.5rem;
}

.btn-close:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-close:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(232, 197, 71, 0.3);
}

.btn-close .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .info-icon {
    width: 56px;
    height: 56px;
  }

  .info-icon .material-symbols-outlined {
    font-size: 2rem;
  }

  .main-message {
    font-size: 0.9375rem;
  }

  .requirements-table {
    padding: 1.25rem;
  }

  .requirement-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .points-required {
    align-items: flex-start;
  }
}
</style>
